import { useState, useEffect, useMemo, useRef } from 'react'
import { Calculator, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import type { TradeRecommendation } from '../../types'
import { fetchOHLCV } from '../../api/client'

interface Props {
  // 実際の売買シグナル(ADX/RSI/MACDルールフィルタ通過済み)。AI軍師の自己申告的な
  // trade_recommendationとは別物で、実データでバックテスト検証済みの本番シグナル。
  signal:       TradeRecommendation | null
  currentPrice: number | null
  symbol:       string | null
}

type AssetType = 'jpy' | 'usd'

function getAssetType(symbol: string): AssetType {
  const s = symbol.toUpperCase()
  if (s.endsWith('.T') || s.includes('JPY')) return 'jpy'
  return 'usd'
}

function assetLabel(symbol: string): string {
  const s = symbol.toUpperCase()
  if (s.endsWith('.T'))        return '国内株式（円建て）'
  if (s.includes('JPY'))       return 'FX 円建てペア'
  if (s.endsWith('=X'))        return 'FX ドル建てペア'
  if (isCrypto(symbol))        return '暗号通貨（USD建て）'
  return '米国株・その他（USD建て）'
}

function isCrypto(symbol: string): boolean {
  const s = symbol.toUpperCase()
  return s.endsWith('USDT') || s.endsWith('BTC') || s.endsWith('ETH')
}

// 暗号通貨はBTC等の高額資産だとリスク許容額から逆算した数量が1単位未満に
// なるのが通常のため、株式・FXのような整数単位への切り捨てだと常に0になって
// しまう。暗号通貨のみ小数点以下6桁まで許容する。
function qtyDecimalsFor(symbol: string | null): number {
  return symbol && isCrypto(symbol) ? 6 : 0
}

function fmt(n: number, dec = 0) {
  return n.toLocaleString('ja-JP', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}

function InputRow({
  label, value, onChange, placeholder, unit, note,
}: {
  label: string; value: string; onChange: (v: string) => void
  placeholder?: string; unit?: string; note?: string
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] text-trading-muted uppercase tracking-wider">{label}</label>
      <div className="flex items-center gap-1.5">
        <input
          type="number" min="0" step="any"
          className="input-field flex-1 text-sm"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {unit && <span className="text-[11px] text-trading-muted flex-none">{unit}</span>}
      </div>
      {note && <p className="text-[9px] text-trading-muted/60">{note}</p>}
    </div>
  )
}

function ResultRow({
  label, value, sub, highlight,
}: { label: string; value: string; sub?: string; highlight?: 'green' | 'yellow' | 'red' }) {
  const colorMap = { green: 'text-trading-green', yellow: 'text-trading-yellow', red: 'text-trading-red' }
  const color = highlight ? colorMap[highlight] : 'text-trading-text'
  return (
    <div className="flex items-baseline justify-between py-1.5 border-b border-trading-border/30 last:border-0">
      <span className="text-[11px] text-trading-muted">{label}</span>
      <span className={clsx('text-sm font-bold tabular-nums', color)}>
        {value}
        {sub && <span className="text-[10px] text-trading-muted font-normal ml-1">{sub}</span>}
      </span>
    </div>
  )
}

export default function PositionSizingForm({ signal, currentPrice, symbol }: Props) {
  const [open, setOpen]               = useState(false)
  const [balance, setBalance]         = useState('')
  const [maxQty, setMaxQty]           = useState('')
  const [riskPct, setRiskPct]         = useState('2')
  const [entryPrice, setEntryPrice]   = useState('')
  const [takeProfit, setTakeProfit]   = useState('')
  const [stopLoss, setStopLoss]       = useState('')
  const [fxRate, setFxRate]           = useState('150')
  const [fxRateLoading, setFxRateLoading] = useState(false)
  const [leverage, setLeverage]       = useState('')
  const [accountRate, setAccountRate] = useState('')
  const [spread, setSpread]           = useState('')
  const fxFetchedRef = useRef(false)

  const assetType = symbol ? getAssetType(symbol) : 'usd'
  const isJpy     = assetType === 'jpy'

  // 計算機を開いた際に USD/JPY レートを自動取得（初回のみ）
  useEffect(() => {
    if (!open || fxFetchedRef.current) return
    fxFetchedRef.current = true
    setFxRateLoading(true)
    fetchOHLCV('USDJPY=X', '1d', 1)
      .then(res => {
        const close = res.data[res.data.length - 1]?.close
        if (close && close > 0) setFxRate(String(Math.round(close * 100) / 100))
      })
      .catch(() => { /* デフォルト 150 を維持 */ })
      .finally(() => setFxRateLoading(false))
  }, [open])

  // 銘柄変更時に価格フィールドをリセット
  useEffect(() => {
    setEntryPrice(currentPrice != null ? String(currentPrice) : '')
    const sl = signal?.stop_loss
    setStopLoss(sl != null ? String(sl) : '')
    const tp = signal?.take_profit
    setTakeProfit(tp != null ? String(tp) : '')
    setAccountRate('')
    setSpread('')
  }, [symbol]) // eslint-disable-line react-hooks/exhaustive-deps

  function fillFromSignal() {
    if (signal?.entry_price != null) setEntryPrice(String(signal.entry_price))
    else if (currentPrice != null) setEntryPrice(String(currentPrice))
    if (signal?.stop_loss != null) setStopLoss(String(signal.stop_loss))
    if (signal?.take_profit != null) setTakeProfit(String(signal.take_profit))
  }

  // 口座の実勢レート・スプレッドから、実際に発注する利確/損切りレートを算出する。
  // エントリーは口座の現在レートをそのまま使う(それが実際に約定するレートその
  // ものなので、追加のシフトは加えない)。TP/SLは、システム側(Chronosのデータ
  // ソース)のレートと口座の実勢レートのズレ + スプレッド(BUYはask側=上振れ、
  // SELLはbid側=下振れ)を、システム側の値からの同一シフト量として反映する。
  const orderLevels = useMemo(() => {
    const entry = parseFloat(entryPrice)
    const sl    = parseFloat(stopLoss)
    const tp    = parseFloat(takeProfit)
    const acct  = parseFloat(accountRate)
    const spr   = parseFloat(spread) || 0

    if (!entry || entry <= 0 || !acct || acct <= 0) return null

    const dir: 'BUY' | 'SELL' | null =
      signal?.action === 'BUY' || signal?.action === 'SELL' ? signal.action
      : sl > 0 && sl < entry ? 'BUY'
      : sl > 0 && sl > entry ? 'SELL'
      : null
    if (!dir) return null

    const rateDiff = acct - entry
    const shift     = rateDiff + (dir === 'BUY' ? spr / 2 : -spr / 2)

    return {
      dir, rateDiff, shift,
      entry:      acct,
      stopLoss:   sl > 0 ? sl + shift : null,
      takeProfit: tp > 0 ? tp + shift : null,
    }
  }, [entryPrice, stopLoss, takeProfit, accountRate, spread, signal])

  // --- 計算 ---
  const result = useMemo(() => {
    const bal   = parseFloat(balance)
    const maxQ  = parseFloat(maxQty)
    const rPct  = parseFloat(riskPct)
    const entry = parseFloat(entryPrice)
    const sl    = parseFloat(stopLoss)
    const fx    = parseFloat(fxRate) || 150
    const lev   = parseFloat(leverage) || 1

    if (!bal || !entry || !sl || bal <= 0 || entry <= 0 || sl <= 0 || rPct <= 0) return null

    const slDist   = Math.abs(entry - sl)
    if (slDist === 0) return null

    const slPct    = (slDist / entry) * 100
    const riskJpy  = bal * (rPct / 100)

    // 固定フラクショナル法
    const qtyRaw     = isJpy ? riskJpy / slDist : (riskJpy / fx) / slDist
    const qtyScale   = Math.pow(10, qtyDecimalsFor(symbol))
    const qty        = Math.floor(qtyRaw * qtyScale) / qtyScale
    const finalQty   = maxQ > 0 ? Math.min(qty, maxQ) : qty
    const isCapped   = maxQ > 0 && qty > maxQ

    // 必要証拠金 = 想定元本 ÷ レバレッジ
    const notionalJpy   = isJpy ? finalQty * entry : finalQty * entry * fx
    const marginJpy     = notionalJpy / lev
    const marginPct     = (marginJpy / bal) * 100
    const actualRiskJpy = isJpy ? finalQty * slDist : finalQty * slDist * fx

    // 期待値（実シグナルの実績勝率・R/R）
    const winProb = signal?.win_probability   ?? null
    const rr      = signal?.risk_reward_ratio ?? null
    const ev      = (winProb != null && rr != null)
      ? winProb * rr - (1 - winProb)
      : null
    const evJpy   = (ev != null) ? ev * actualRiskJpy : null

    return {
      riskJpy, slDist, slPct, finalQty, isCapped,
      notionalJpy, marginJpy, marginPct,
      actualRiskJpy, winProb, rr, ev, evJpy, lev,
    }
  }, [balance, maxQty, riskPct, entryPrice, stopLoss, fxRate, leverage, isJpy, signal, symbol])

  const rec = signal

  return (
    <div className="rounded-lg border border-trading-border bg-trading-panel overflow-hidden">
      {/* ヘッダー */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-trading-border/20 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Calculator className="w-4 h-4 text-trading-blue flex-none" />
          <span className="text-sm font-semibold text-trading-text">ポジション計算機</span>
          {symbol && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-trading-border text-trading-muted">
              {assetLabel(symbol)}
            </span>
          )}
          {result && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-trading-green/20 text-trading-green border border-trading-green/30 font-bold">
              推奨 {fmt(result.finalQty, qtyDecimalsFor(symbol))} 単位
            </span>
          )}
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-trading-muted flex-none" />
          : <ChevronDown className="w-4 h-4 text-trading-muted flex-none" />}
      </button>

      {open && (
        <div className="border-t border-trading-border px-4 py-4 space-y-4">

          {/* 実シグナルのサマリー（R/R・実績勝率） */}
          {rec && (rec.win_probability != null || rec.risk_reward_ratio != null) && (
            <div className="flex flex-wrap gap-2 p-2.5 rounded-md bg-trading-blue/5 border border-trading-blue/20">
              <span className="text-[10px] text-trading-muted self-center">実シグナル参考値:</span>
              {rec.win_probability != null && (
                <span className="text-[11px] font-bold text-trading-text">
                  実績勝率 <span className="text-trading-green">{(rec.win_probability * 100).toFixed(0)}%</span>
                </span>
              )}
              {rec.risk_reward_ratio != null && (
                <span className="text-[11px] font-bold text-trading-text">
                  R/R比 <span className="text-trading-blue">{rec.risk_reward_ratio.toFixed(2)}</span>
                </span>
              )}
              {rec.win_probability != null && rec.risk_reward_ratio != null && (() => {
                const ev = rec.win_probability * rec.risk_reward_ratio - (1 - rec.win_probability)
                return (
                  <span className="text-[11px] font-bold text-trading-text">
                    期待値/R <span className={ev > 0 ? 'text-trading-green' : 'text-trading-red'}>{ev > 0 ? '+' : ''}{ev.toFixed(2)}</span>
                  </span>
                )
              })()}
            </div>
          )}

          {/* 入力フォーム */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InputRow label="現在の残高" value={balance} onChange={setBalance} placeholder="1000000" unit="円" />
            <InputRow label="最大建玉可能数量" value={maxQty} onChange={setMaxQty} placeholder="空欄 = 制限なし" unit="単位" />
            <InputRow label="リスク許容割合" value={riskPct} onChange={setRiskPct} placeholder="2" unit="%" note="推奨: 1〜3%" />
            <InputRow
              label="レバレッジ"
              value={leverage} onChange={setLeverage} placeholder="空欄 = 1倍（現物）"
              unit="倍" note="FX: 25 / 暗号: 最大値を入力"
            />
            <InputRow
              label="エントリー価格"
              value={entryPrice} onChange={setEntryPrice} placeholder="0"
              unit={isJpy ? '円' : 'USD'}
            />
            <InputRow
              label="利確価格"
              value={takeProfit} onChange={setTakeProfit} placeholder="0"
              unit={isJpy ? '円' : 'USD'}
            />
            <InputRow
              label="損切り価格"
              value={stopLoss} onChange={setStopLoss} placeholder="0"
              unit={isJpy ? '円' : 'USD'}
            />
            {!isJpy && (
              <InputRow
                label={fxRateLoading ? 'USD/JPY レート (取得中…)' : 'USD/JPY レート'}
                value={fxRate} onChange={setFxRate} placeholder="150" unit="円"
                note="自動取得 · 手動で上書き可"
              />
            )}
          </div>

          {/* 実シグナルから自動入力 */}
          {rec?.stop_loss != null && (
            <button onClick={fillFromSignal} className="flex items-center gap-1.5 text-[11px] text-trading-blue hover:opacity-70 transition-opacity">
              <RefreshCw className="w-3 h-3" />
              実シグナルの値（エントリー・利確・損切り）を入力
            </button>
          )}

          {/* 口座レート・スプレッド補正 */}
          <div className="rounded-md border border-trading-border/60 px-3 py-2.5 space-y-2">
            <p className="text-[10px] text-trading-muted uppercase tracking-wider">口座レート補正（任意）</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InputRow
                label="口座の現在レート" value={accountRate} onChange={setAccountRate}
                placeholder="0" unit={isJpy ? '円' : 'USD'}
                note="上のエントリー価格との差分を自動算出"
              />
              <InputRow
                label="スプレッド" value={spread} onChange={setSpread}
                placeholder="0" unit={isJpy ? '円' : 'USD'}
                note="BUY=ask基準/SELL=bid基準で自動シフト"
              />
            </div>
            {orderLevels && (
              <div className="rounded bg-trading-bg border border-trading-border/50 px-2.5 py-2 space-y-1">
                <p className="text-[9px] text-trading-muted">
                  口座レート差 {orderLevels.rateDiff >= 0 ? '+' : ''}{orderLevels.rateDiff.toFixed(isJpy ? 3 : 5)}
                  ・{orderLevels.dir}方向シフト {orderLevels.shift >= 0 ? '+' : ''}{orderLevels.shift.toFixed(isJpy ? 3 : 5)}
                </p>
                <ResultRow label="発注エントリー" value={isJpy ? `¥${fmt(orderLevels.entry, 3)}` : orderLevels.entry.toFixed(5)} highlight="green" />
                {orderLevels.takeProfit != null && (
                  <ResultRow label="発注利確(TP)" value={isJpy ? `¥${fmt(orderLevels.takeProfit, 3)}` : orderLevels.takeProfit.toFixed(5)} />
                )}
                {orderLevels.stopLoss != null && (
                  <ResultRow label="発注損切り(SL)" value={isJpy ? `¥${fmt(orderLevels.stopLoss, 3)}` : orderLevels.stopLoss.toFixed(5)} highlight="red" />
                )}
              </div>
            )}
          </div>

          {/* 計算結果 */}
          {result ? (
            <div className="rounded-md bg-trading-bg border border-trading-border px-3 py-2">
              <p className="text-[10px] text-trading-muted uppercase tracking-wider mb-2">計算結果</p>

              <ResultRow label="リスク許容額" value={`¥${fmt(result.riskJpy)}`} />
              <ResultRow
                label="損切り幅"
                value={isJpy ? `¥${fmt(result.slDist, 2)}` : result.slDist.toFixed(4)}
                sub={`(${result.slPct.toFixed(2)}%)`}
              />
              <ResultRow
                label="推奨数量"
                value={`${fmt(result.finalQty, qtyDecimalsFor(symbol))} 単位`}
                sub={result.isCapped ? '← 最大建玉で制限' : undefined}
                highlight="green"
              />
              <ResultRow
                label="実質リスク額"
                value={`¥${fmt(result.actualRiskJpy)}`}
                highlight={result.actualRiskJpy > parseFloat(balance) * 0.05 ? 'red' : 'yellow'}
              />
              <ResultRow
                label={result.lev > 1 ? `必要証拠金（概算 ÷${result.lev}倍）` : '必要投資額（概算）'}
                value={`¥${fmt(result.marginJpy)}`}
                sub={`残高の ${result.marginPct.toFixed(1)}%`}
                highlight={result.marginPct > 50 ? 'red' : result.marginPct > 20 ? 'yellow' : undefined}
              />
              {result.lev > 1 && (
                <ResultRow
                  label="想定元本（ノーショナル）"
                  value={`¥${fmt(result.notionalJpy)}`}
                  sub={`証拠金の ${result.lev}倍`}
                />
              )}

              {/* 期待値（実シグナルのR/R・実績勝率が揃った場合のみ） */}
              {result.ev != null && result.evJpy != null && (
                <>
                  <div className="my-2 border-t border-trading-border/50" />
                  <ResultRow
                    label="1R あたりの期待値"
                    value={`${result.ev > 0 ? '+' : ''}${result.ev.toFixed(3)} R`}
                    highlight={result.ev > 0 ? 'green' : 'red'}
                  />
                  <ResultRow
                    label="このトレードの期待損益"
                    value={`${result.evJpy >= 0 ? '+' : ''}¥${fmt(result.evJpy)}`}
                    sub={`勝率${(result.winProb! * 100).toFixed(0)}% × R/R${result.rr!.toFixed(2)}`}
                    highlight={result.evJpy > 0 ? 'green' : 'red'}
                  />
                </>
              )}
            </div>
          ) : (
            <p className="text-[11px] text-trading-muted text-center py-2">
              残高・エントリー価格・損切り価格を入力してください
            </p>
          )}

          <p className="text-[9px] text-trading-muted/50 leading-relaxed">
            ※ 固定フラクショナル法による参考値です。手数料・スプレッド・スリッページは考慮していません。実際の取引は自己判断で行ってください。
          </p>
        </div>
      )}
    </div>
  )
}
